import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useAvatar } from './AvatarContext';

// Guided walkthrough of the screen. Each step spotlights one part of the UI
// (everything else is dimmed), the assistant reads the explanation aloud, and the
// caregiver taps Next / Back / Exit. Steps target elements by `data-tour`.

const HIGHLIGHT_PADDING = 10;

function measure(selector) {
  if (!selector) {
    return null;
  }
  const element = document.querySelector(selector);
  if (!element) {
    return null;
  }
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top - HIGHLIGHT_PADDING,
    left: rect.left - HIGHLIGHT_PADDING,
    width: rect.width + HIGHLIGHT_PADDING * 2,
    height: rect.height + HIGHLIGHT_PADDING * 2,
  };
}

export default function GuidedTour({ steps, onFinish }) {
  const { speakText, stopSpeaking } = useAvatar();
  const [index, setIndex] = useState(0);
  const [frame, setFrame] = useState(null);

  const step = steps[index];
  const isLast = index === steps.length - 1;

  // Position the spotlight over the current target; follow resizes.
  useLayoutEffect(() => {
    const update = () => setFrame(measure(step?.target));
    update();
    const settle = setTimeout(update, 150);
    window.addEventListener('resize', update);
    return () => {
      clearTimeout(settle);
      window.removeEventListener('resize', update);
    };
  }, [step]);

  // Read each step aloud as it appears.
  useEffect(() => {
    if (!step) {
      return undefined;
    }
    stopSpeaking();
    const timer = setTimeout(() => speakText(step.text), 300);
    return () => clearTimeout(timer);
  }, [step, speakText, stopSpeaking]);

  const finish = () => {
    stopSpeaking();
    onFinish();
  };

  if (!step) {
    return null;
  }

  return (
    <div className="vica-tour" role="dialog" aria-modal="true" aria-label="Guided tour">
      {frame ? (
        <div className="vica-tour-spotlight" style={frame} />
      ) : (
        <div className="vica-tour-dim" />
      )}

      <div className="vica-tour-card">
        <div className="vica-tour-progress">
          Step {index + 1} of {steps.length}
        </div>
        <h2 className="vica-tour-title">{step.title}</h2>
        <p className="vica-tour-text">{step.text}</p>
        <div className="vica-tour-actions">
          <button type="button" className="vica-btn-secondary vica-tour-btn" onClick={finish}>
            Exit
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              className="vica-btn-secondary vica-tour-btn"
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
              disabled={index === 0}
            >
              Back
            </button>
            <button
              type="button"
              className="vica-btn vica-tour-btn-primary"
              onClick={() => (isLast ? finish() : setIndex((i) => i + 1))}
            >
              {isLast ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// The tour invite is offered once per login: Login sets this flag, and Home
// shows the invite while it is set and clears it once the caregiver answers.
// Returning to Home or refreshing within the same login does not ask again.
const TOUR_OFFER_PENDING_KEY = 'vicaTourOfferPending';

export const markTourOfferPending = () => {
  try {
    localStorage.setItem(TOUR_OFFER_PENDING_KEY, 'true');
  } catch {
    // storage unavailable: no invite this login (Guidance still works)
  }
};

export const isTourOfferPending = () => {
  try {
    return localStorage.getItem(TOUR_OFFER_PENDING_KEY) === 'true';
  } catch {
    return false;
  }
};

export const clearTourOfferPending = () => {
  try {
    localStorage.removeItem(TOUR_OFFER_PENDING_KEY);
  } catch {
    // ignore
  }
};

// Asked before the tour starts. The assistant speaks the question too (audio
// is already unlocked by the "Tap to begin" screen in AppShell).
export function TourInvite({ onAccept, onDecline }) {
  const { speakText, stopSpeaking, assistantName } = useAvatar();
  const question = `Hi, I'm ${assistantName}. Would you like me to show you around VICA? It is very useful and only takes a minute.`;

  useEffect(() => {
    stopSpeaking();
    const timer = setTimeout(() => speakText(question), 300);
    return () => clearTimeout(timer);
  }, [speakText, stopSpeaking, question]);

  return (
    <div className="vica-tour" role="dialog" aria-modal="true" aria-label="Guided tour offer">
      <div className="vica-tour-dim" />
      <div className="vica-tour-card vica-tour-card-center">
        <h2 className="vica-tour-title">Welcome to VICA</h2>
        <p className="vica-tour-text">{question}</p>
        <div className="vica-tour-actions">
          <button
            type="button"
            className="vica-btn-secondary vica-tour-btn"
            onClick={() => {
              stopSpeaking();
              onDecline();
            }}
          >
            No, thanks
          </button>
          <button
            type="button"
            className="vica-btn vica-tour-btn-primary"
            onClick={() => {
              stopSpeaking();
              onAccept();
            }}
          >
            Yes, show me around
          </button>
        </div>
      </div>
    </div>
  );
}
